import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { ScrollManager } from './components/platform/ScrollManager';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import App from './App';
import { queryClient } from './lib/queryClient';
import { CACHE_MAX_AGE_MS, CACHE_SCHEMA, persister } from './lib/persist';
import { PreferencesProvider } from './lib/preferences';
import { initPlatform, isNativeApp } from './lib/platform';
import { hideSplash, initNativeShell } from './lib/native';
import { registerServiceWorker } from './lib/pwa';
import './index.css';
// The PDF export, such as it is: printing goes through the ONE renderer.
import './print.css';

/**
 * Boot.
 *
 * Two things must finish before the first render:
 *
 *   1. `initPlatform()` — reads the persisted session token out of native
 *      storage. Render before this and the very first `/api/me` goes out with no
 *      Authorization header, which reads as "logged out" and bounces the user to
 *      the login screen they were promised they'd never see again.
 *   2. `initNativeShell()` — back button, keyboard, status bar.
 *
 * The persisted query cache is restored by `PersistQueryClientProvider`, which
 * holds the tree back until rehydration finishes. That is what makes a cold
 * offline launch show the library instead of an empty state.
 */
async function boot(): Promise<void> {
  const rootElement = document.getElementById('root');
  if (!rootElement) throw new Error('Root element not found');

  await initPlatform();
  await initNativeShell();

  // The packaged app has no landing page — it IS the platform. Rewrite the entry
  // URL before the router reads it, so there is no visible hop through `/`.
  if (isNativeApp && window.location.pathname === '/') {
    window.history.replaceState(null, '', '/dashboard');
  }

  createRoot(rootElement).render(
    <StrictMode>
      <PersistQueryClientProvider
        client={queryClient}
        persistOptions={{
          persister,
          maxAge: CACHE_MAX_AGE_MS,
          // The CACHE SCHEMA, not the build id. Busting on the build id threw
          // the whole snapshot away every deploy — session included — which is
          // what showed a signed-in user a login screen after each update. The
          // per-deploy drop still happens, selectively, inside the persister
          // (`lib/persist.ts`): content goes, `me` stays.
          buster: CACHE_SCHEMA,
          dehydrateOptions: {
            // Only successful results are worth keeping. Persisting errors would
            // resurrect a failed fetch as though it were an answer.
            shouldDehydrateQuery: (query) => query.state.status === 'success',
          },
        }}
      >
        <BrowserRouter>
          <ScrollManager />
          <PreferencesProvider>
            <App />
          </PreferencesProvider>
        </BrowserRouter>
      </PersistQueryClientProvider>
    </StrictMode>,
  );

  // Next frame: the tree has painted, so the splash can go without a white flash.
  requestAnimationFrame(() => {
    void hideSplash();
  });

  // Web only, and after first paint — precaching the shell must never compete
  // with rendering it.
  void registerServiceWorker();
}

void boot();
